import type { Address, Plan, SubscriptionStatus } from "./schemas";

const moneyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  minimumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export function formatMoney(amountMinor: number): string {
  return moneyFormatter.format(amountMinor / 100);
}

export function formatDate(value?: string): string {
  if (!value) return "—";
  return dateFormatter.format(new Date(value));
}

export function formatDateTime(value?: string): string {
  if (!value) return "—";
  return dateTimeFormatter.format(new Date(value));
}

export function frequencyLabel(plan: Pick<Plan, "frequency" | "intervalCount">): string {
  const labels = {
    WEEKLY: "hafta",
    MONTHLY: "ay",
    YEARLY: "yıl",
  } as const;
  return plan.intervalCount === 1
    ? `Her ${labels[plan.frequency]}`
    : `Her ${plan.intervalCount} ${labels[plan.frequency]}`;
}

export const subscriptionStatusLabels: Record<SubscriptionStatus, string> = {
  CHECKOUT_PENDING: "Ödeme bekleniyor",
  ACTIVE: "Aktif",
  PAST_DUE: "Ödeme gerekli",
  CANCELLATION_PENDING: "İptal ediliyor",
  CANCELED: "İptal edildi",
  EXPIRED: "Sona erdi",
  SYNC_ERROR: "İşlem kontrol ediliyor",
};

export function subscriptionStatusTone(
  status: SubscriptionStatus,
): "positive" | "warning" | "danger" | "neutral" {
  if (status === "ACTIVE") return "positive";
  if (status === "PAST_DUE" || status === "CHECKOUT_PENDING") return "warning";
  if (status === "SYNC_ERROR") return "danger";
  return "neutral";
}

export function formatAddress(address: Address): string {
  return [
    address.address1,
    address.address2,
    `${address.district} / ${address.city}`,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
}

export function safeInternalPath(value: string | null | undefined, fallback = "/subscriptions"): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return fallback;
  return value;
}

export function initials(name: string): string {
  return name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase("tr-TR") ?? "")
    .join("");
}
